import React, { useState } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  CreditCardIcon,
  PlusIcon,
  BanknotesIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import PaymentForm from '../../components/payments/PaymentForm';
import PaymentHistory from '../../components/payments/PaymentHistory';
import InvoiceGenerator from '../../components/payments/InvoiceGenerator';
import FinancialReports from '../../components/payments/FinancialReports';

const PaymentsPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('this-month');

  // Fetch payments data
  const { data: paymentsData, isLoading } = useQuery('payments', async () => {
    const response = await axios.get('/payments');
    return response.data;
  });

  // Fetch financial statistics
  const { data: financialStats } = useQuery('financial-stats', async () => {
    const response = await axios.get('/payments/stats');
    return response.data;
  });

  const payments = paymentsData?.payments || [];
  const stats = financialStats?.stats || {};

  // Calculate quick stats
  const quickStats = {
    totalRevenue: payments.reduce((sum, payment) => sum + (payment.amount || 0), 0),
    paidToday: payments.filter(p => {
      const today = new Date().toDateString();
      return new Date(p.paymentDate).toDateString() === today;
    }).reduce((sum, payment) => sum + (payment.amount || 0), 0),
    pendingPayments: payments.filter(p => p.status === 'pending').length,
    overduePayments: payments.filter(p => p.status === 'overdue').length,
    monthlyGrowth: stats.monthlyGrowth || 0,
  };

  const tabs = [
    { id: 'overview', name: 'Vue d\'ensemble', icon: ChartBarIcon },
    { id: 'payments', name: 'Paiements', icon: CreditCardIcon },
    { id: 'invoices', name: 'Factures', icon: DocumentTextIcon },
    { id: 'reports', name: 'Rapports', icon: ArrowTrendingUpIcon },
  ];

  const paymentMethods = [
    { id: 'cash', name: 'Espèces', icon: BanknotesIcon, color: 'bg-green-500' },
    { id: 'card', name: 'Carte', icon: CreditCardIcon, color: 'bg-blue-500' },
    { id: 'transfer', name: 'Virement', icon: ArrowTrendingUpIcon, color: 'bg-purple-500' },
    { id: 'check', name: 'Chèque', icon: DocumentTextIcon, color: 'bg-orange-500' },
  ];

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">
                {t('payments')}
              </h1>
              <p className="text-white/60 mt-2">
                Gestion complète des paiements et facturation
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/payments/invoice/new"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 flex items-center space-x-2"
              >
                <DocumentTextIcon className="h-5 w-5" />
                <span>Nouvelle Facture</span>
              </Link>
              <Link
                to="/payments/new"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Nouveau Paiement</span>
              </Link>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">Revenus Total</p>
                  <p className="text-2xl font-bold text-white mt-2">
                    {quickStats.totalRevenue.toLocaleString()} DA
                  </p>
                  <div className="flex items-center mt-2">
                    {quickStats.monthlyGrowth >= 0 ? (
                      <ArrowTrendingUpIcon className="h-4 w-4 text-green-400 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-4 w-4 text-red-400 mr-1" />
                    )}
                    <span className={`text-sm ${quickStats.monthlyGrowth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {Math.abs(quickStats.monthlyGrowth)}% ce mois
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-2xl bg-green-500/20">
                  <BanknotesIcon className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">Payé Aujourd'hui</p>
                  <p className="text-2xl font-bold text-white mt-2">
                    {quickStats.paidToday.toLocaleString()} DA
                  </p>
                  <p className="text-white/50 text-sm mt-2">
                    {payments.filter(p => {
                      const today = new Date().toDateString();
                      return new Date(p.paymentDate).toDateString() === today;
                    }).length} transactions
                  </p>
                </div>
                <div className="p-3 rounded-2xl bg-blue-500/20">
                  <CreditCardIcon className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">Paiements en Attente</p>
                  <p className="text-2xl font-bold text-white mt-2">{quickStats.pendingPayments}</p>
                  <p className="text-white/50 text-sm mt-2">À traiter</p>
                </div>
                <div className="p-3 rounded-2xl bg-yellow-500/20">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">Paiements en Retard</p>
                  <p className="text-2xl font-bold text-white mt-2">{quickStats.overduePayments}</p>
                  <p className="text-white/50 text-sm mt-2">Nécessitent un suivi</p>
                </div>
                <div className="p-3 rounded-2xl bg-red-500/20">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Payment Methods Overview */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-xl font-semibold text-white mb-4">
              Répartition par Mode de Paiement
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {paymentMethods.map((method) => {
                const methodPayments = payments.filter(p => p.paymentMethod === method.id);
                const methodTotal = methodPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
                const percentage = quickStats.totalRevenue > 0 ? (methodTotal / quickStats.totalRevenue * 100) : 0;
                
                return (
                  <div key={method.id} className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-10 h-10 ${method.color} rounded-xl flex items-center justify-center`}>
                        <method.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{method.name}</h4>
                        <p className="text-white/60 text-sm">{methodPayments.length} paiements</p>
                      </div>
                    </div>
                    <p className="text-lg font-bold text-white">{methodTotal.toLocaleString()} DA</p>
                    <p className="text-white/50 text-sm">{percentage.toFixed(1)}% du total</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[500px]">
            {activeTab === 'overview' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                {/* Recent Payments */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-white">
                      Paiements Récents
                    </h3>
                    <Link
                      to="/payments/history"
                      className="text-blue-400 hover:text-blue-300 text-sm"
                    >
                      Voir tout
                    </Link>
                  </div>
                  {isLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/30"></div>
                    </div>
                  ) : payments.length > 0 ? (
                    <div className="space-y-3">
                      {payments.slice(0, 5).map((payment) => (
                        <div
                          key={payment.id}
                          className="flex items-center justify-between p-3 bg-white/5 rounded-xl"
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              paymentMethods.find(m => m.id === payment.paymentMethod)?.color || 'bg-gray-500'
                            }`}>
                              {React.createElement(
                                paymentMethods.find(m => m.id === payment.paymentMethod)?.icon || CreditCardIcon,
                                { className: "h-4 w-4 text-white" }
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{payment.memberName}</p>
                              <p className="text-white/60 text-sm">{payment.description}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-semibold">{payment.amount?.toLocaleString()} DA</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              payment.status === 'paid' 
                                ? 'bg-green-500/20 text-green-400' 
                                : payment.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {payment.status === 'paid' ? 'Payé' : 
                               payment.status === 'pending' ? 'En attente' : 'En retard'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CreditCardIcon className="h-12 w-12 text-white/30 mx-auto mb-4" />
                      <p className="text-white/60">Aucun paiement enregistré</p>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Actions Rapides
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    <Link
                      to="/payments/new"
                      className="group flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <PlusIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">Nouveau Paiement</h4>
                        <p className="text-white/60 text-sm">Enregistrer un paiement</p>
                      </div>
                    </Link>

                    <Link
                      to="/payments/invoice/new"
                      className="group flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <DocumentTextIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">Créer une Facture</h4>
                        <p className="text-white/60 text-sm">Générer une nouvelle facture</p>
                      </div>
                    </Link>

                    <Link
                      to="/payments/reports"
                      className="group flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <ChartBarIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">Rapports Financiers</h4>
                        <p className="text-white/60 text-sm">Analyser les revenus</p>
                      </div>
                    </Link>

                    <button className="group flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <DocumentArrowDownIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">Exporter les Données</h4>
                        <p className="text-white/60 text-sm">Télécharger les rapports</p>
                      </div>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'payments' && <PaymentHistory payments={payments} />}
            {activeTab === 'invoices' && <InvoiceGenerator />}
            {activeTab === 'reports' && <FinancialReports stats={stats} />}
          </div>
        </div>
      } />
      <Route path="/new" element={<PaymentForm />} />
      <Route path="/:id/edit" element={<PaymentForm />} />
      <Route path="/invoice/new" element={<InvoiceGenerator />} />
      <Route path="/invoice/:id" element={<InvoiceGenerator />} />
      <Route path="/reports" element={<FinancialReports />} />
      <Route path="/history" element={<PaymentHistory payments={payments} />} />
    </Routes>
  );
};

export default PaymentsPage; 